import { Input } from '../../../../shared/components/FormControls';
import { useState, useRef, useEffect, useCallback } from 'react';
import Rodal from 'rodal';
import { Button } from '../../../../shared/components/Button';
import '../../../../shared/styles/Orchestrator.css';
import 'rodal/lib/rodal.css';
import {
  LayersPlus,
  Eye,
  EyeOff,
  Monitor,
  MonitorOff,
  Trash2,
  Upload,
  Share2,
  X,
} from 'lucide-react';

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 10;

/**
 * WorldMapModal – full-screen world map viewer/editor.
 *
 * GM mode (isGm=true): add layers, toggle player visibility, delete layers.
 * Player mode (isGm=false): view player-visible layers only (handled by caller
 * passing only visible layers).
 *
 * Props:
 *   visible               – boolean
 *   worldMap              – { layers: [{ id, name, imageUrl, z, visible, playerVisible }] }
 *   isGm                  – boolean
 *   mode                  – 'light' | 'dark'
 *   onClose               – () => void
 *   onSave                – (worldMap) => void  (GM only)
 *   isLoading             – boolean
 *   displayOverlayActive  – boolean  (GM only) whether the map overlay is on the display
 *   onToggleDisplayOverlay – () => void  (GM only)
 *   onViewChange          – (view: {panX, panY, zoom}) => void
 *   initialView           – {panX, panY, zoom}
 */
export default function WorldMapModal({
  visible,
  worldMap,
  isGm = false,
  mode = 'light',
  onClose,
  onSave,
  isLoading = false,
  displayOverlayActive = false,
  onToggleDisplayOverlay,
  playerShareActive = false,
  onTogglePlayerShare,
  onViewChange,
  initialView,
}) {
  const isDark = mode === 'dark';
  const layers = (worldMap?.layers || []).slice().sort((a, b) => (a.z || 0) - (b.z || 0));

  const fileInputRef = useRef(null);
  const [pendingLayerName, setPendingLayerName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Track which layer the user is currently viewing (by clicking in list)
  const [focusedLayerId, setFocusedLayerId] = useState(null);

  // Zoom/pan state for the map display area
  const [mapView, setMapView] = useState(
    () => initialView || { panX: 0, panY: 0, zoom: 1 }
  );
  const mapContainerRef = useRef(null);
  const mapDragRef = useRef(null);
  const mapViewRef = useRef(mapView);
  const onViewChangeRef = useRef(onViewChange);
  useEffect(() => { onViewChangeRef.current = onViewChange; }, [onViewChange]);
  // Keep mapViewRef in sync so pointer handlers always read the latest view
  useEffect(() => { mapViewRef.current = mapView; }, [mapView]);

  // Non-passive wheel listener for zoom
  useEffect(() => {
    const el = mapContainerRef.current;
    if (!el) return undefined;
    const handleWheel = (e) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.1 : 0.9;
      setMapView((prev) => {
        const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev.zoom * factor));
        const rect = el.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;
        const scale = newZoom / prev.zoom;
        const newView = {
          panX: mx - scale * (mx - prev.panX),
          panY: my - scale * (my - prev.panY),
          zoom: newZoom,
        };
        onViewChangeRef.current?.(newView);
        return newView;
      });
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  const handleMapPointerDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const current = mapViewRef.current;
    mapDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      panX: current.panX,
      panY: current.panY,
      zoom: current.zoom,
    };
  }, []);

  const handleMapPointerMove = useCallback((e) => {
    if (!mapDragRef.current) return;
    const dx = e.clientX - mapDragRef.current.startX;
    const dy = e.clientY - mapDragRef.current.startY;
    const newView = {
      panX: mapDragRef.current.panX + dx,
      panY: mapDragRef.current.panY + dy,
      zoom: mapDragRef.current.zoom,
    };
    setMapView(newView);
    onViewChangeRef.current?.(newView);
  }, []);

  const handleMapPointerUp = useCallback(() => {
    mapDragRef.current = null;
  }, []);

  const handleMapDoubleClick = useCallback(() => {
    const newView = { panX: 0, panY: 0, zoom: 1 };
    setMapView(newView);
    onViewChangeRef.current?.(newView);
  }, []);

  const handleAddLayerClick = () => {
    setUploadError(null);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    event.target.value = '';

    setIsUploading(true);
    setUploadError(null);
    try {
      const { uploadDataUrl } = await import('../../utils/storageUpload');
      const { resizeImage } = await import('../../utils/imageUtils');

      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const resized = await resizeImage(dataUrl, 4096, Infinity);
      const imageUrl = await uploadDataUrl(resized, 'worldmap');

      const nextZ = layers.length > 0 ? Math.max(...layers.map((l) => l.z || 0)) + 1 : 1;
      const name = pendingLayerName.trim() || file.name.replace(/\.[^.]+$/, '') || `Layer ${nextZ}`;
      const newLayer = {
        id: `wm_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        name,
        imageUrl,
        z: nextZ,
        visible: true,
        playerVisible: true,
      };

      const updated = { layers: [...(worldMap?.layers || []), newLayer] };
      onSave?.(updated);
      setPendingLayerName('');
    } catch (err) {
      console.error('[WorldMapModal] Upload failed', err);
      setUploadError('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const toggleLayerVisible = (layerId) => {
    const updated = {
      layers: (worldMap?.layers || []).map((l) =>
        l.id === layerId ? { ...l, visible: l.visible === false } : l
      ),
    };
    onSave?.(updated);
  };

  const deleteLayer = (layerId) => {
    const updated = {
      layers: (worldMap?.layers || []).filter((l) => l.id !== layerId),
    };
    if (focusedLayerId === layerId) setFocusedLayerId(null);
    onSave?.(updated);
  };

  const focusedLayer = focusedLayerId ? layers.find((l) => l.id === focusedLayerId) : null;

  return (
    <Rodal
      visible={visible}
      onClose={onClose}
      animation="slideUp"
      customStyles={{
        width: '100%',
        height: '100%',
        maxWidth: '100vw',
        maxHeight: '100vh',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        margin: 0,
        padding: 0,
        borderRadius: 0,
        background: isDark ? '#26292C' : 'var(--ivory)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      closeButton={false}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 16px',
          background: isDark ? '#393e41' : 'var(--ivory)',
          borderBottom: '2px solid #e94f37',
          flexShrink: 0,
        }}
      >
        <span style={{ color: isDark ? '#fff' : 'var(--jet)', fontWeight: 700, fontSize: 16 }}>
          World Map
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {isGm && (
            <>
              <Input
                type="text"
                placeholder="Layer name (optional)"
                value={pendingLayerName}
                onChange={(e) => setPendingLayerName(e.target.value)}
                style={{
                  boxSizing: 'border-box',
                  height: 34,
                  minHeight: 34,
                  background: isDark ? 'var(--onyx)' : 'var(--ivory)',
                  border: isDark ? '2px solid var(--ivory)' : '2px solid #ccc',
                  borderRadius: 3,
                  color: isDark ? 'var(--ivory)' : 'var(--jet)',
                  width: 200,
                }}
              />
              <Button
                icon={LayersPlus}
                label={isUploading ? 'Uploading…' : 'Add Layer'}
                type="cinnabar"
                disabled={isUploading || isLoading}
                onPress={handleAddLayerClick}
              />
              <Input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <Button
                icon={displayOverlayActive ? MonitorOff : Monitor}
                label={displayOverlayActive ? 'Hide from Display' : 'Show on Display'}
                type={displayOverlayActive ? 'blue' : 'cinnabar'}
                onPress={onToggleDisplayOverlay}
              />
              <Button
                icon={Share2}
                label={playerShareActive ? 'Stop Sharing' : 'Share with Players'}
                type={playerShareActive ? 'blue' : 'cinnabar'}
                onPress={onTogglePlayerShare}
              />
            </>
          )}
          <Button icon={X} description="Close" type={isDark ? 'onyx' : 'black-white'} onPress={onClose} />
        </div>
      </div>

      {uploadError && (
        <div style={{ background: '#c0392b', color: '#fff', padding: '6px 16px', fontSize: 13 }}>
          {uploadError}
        </div>
      )}

      {/* Body */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* Sidebar: layer list (GM only) */}
        {isGm && (
          <div
            style={{
              width: 220,
              background: isDark ? '#393e41' : 'var(--ivory)',
              borderRight: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #ddd',
              overflowY: 'auto',
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                padding: '8px 12px',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: 'uppercase',
                color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.55)',
                borderBottom: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #ddd',
              }}
            >
              Layers
            </div>
            {layers.length === 0 && (
              <div style={{ color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.45)', fontSize: 13, padding: 12 }}>
                No layers yet. Add one above.
              </div>
            )}
            {layers.map((layer) => (
              <div
                key={layer.id}
                onClick={() => setFocusedLayerId(layer.id === focusedLayerId ? null : layer.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '7px 10px',
                  cursor: 'pointer',
                  background:
                    focusedLayerId === layer.id
                      ? 'rgba(233,79,55,0.18)'
                      : 'transparent',
                  borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #eee',
                  color: layer.visible === false
                    ? (isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)')
                    : (isDark ? '#fff' : 'var(--jet)'),
                }}
              >
                <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {layer.name}
                </span>
                <button
                  title={layer.visible === false ? 'Show layer' : 'Hide layer'}
                  onClick={(e) => { e.stopPropagation(); toggleLayerVisible(layer.id); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: layer.visible === false ? 'rgba(255,255,255,0.3)' : '#e94f37',
                    padding: 2,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {layer.visible === false ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
                <button
                  title="Delete layer"
                  onClick={(e) => { e.stopPropagation(); deleteLayer(layer.id); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'rgba(255,100,80,0.7)',
                    padding: 2,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Map display area */}
        <div
          ref={mapContainerRef}
          style={{
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
            background: isDark ? '#111' : 'var(--ivory)',
            cursor: mapDragRef.current ? 'grabbing' : 'grab',
            userSelect: 'none',
          }}
          onPointerDown={handleMapPointerDown}
          onPointerMove={handleMapPointerMove}
          onPointerUp={handleMapPointerUp}
          onPointerLeave={handleMapPointerUp}
          onDoubleClick={handleMapDoubleClick}
        >
          {layers.length === 0 && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.35)',
                fontSize: 16,
                flexDirection: 'column',
                gap: 12,
                pointerEvents: 'none',
              }}
            >
              {isGm ? (
                <>
                  <Upload size={36} style={{ opacity: 0.3 }} />
                  <span>Add a layer to get started</span>
                </>
              ) : (
                <span>No map available</span>
              )}
            </div>
          )}

          {/* Render all layers stacked with pan/zoom transform */}
          {layers.filter((layer) => layer.visible !== false).map((layer) => (
            <img
              key={layer.id}
              src={layer.imageUrl}
              alt={layer.name}
              draggable={false}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                zIndex: layer.z || 1,
                transform: `translate(${mapView.panX}px, ${mapView.panY}px) scale(${mapView.zoom})`,
                transformOrigin: 'center center',
                pointerEvents: 'none',
              }}
            />
          ))}

          {/* Zoom hint */}
          <div
            style={{
              position: 'absolute',
              bottom: 8,
              right: 10,
              color: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.4)',
              fontSize: 11,
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          >
            Scroll to zoom · Drag to pan · Double-click to reset
          </div>
        </div>
      </div>
    </Rodal>
  );
}
